import { CustomButton, DropdownMultiSelect, DropdownSelect } from '@mercadoni/gungnir'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import DrawerFormCapacity from '../../domain/resource-management/people/capacity/capacity.component'

import { useResourceProfileStore } from './resource-profile.context'
import { getFleetsQuotas, mapQuotas, DEFAULT_COLORS } from './resource-profile.tools'

import './resource-profile.styles.less'
import { Can } from '../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { SubModulePerms } from '../../refactory/modules/access-control/domain/module'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { ResourceQuotasCalendar } from '../../refactory/modules/resource-management/infrastructure/resource-quotas-calendar/resource-quotas-calendar'
import { QuotasConfirmAction } from '../../refactory/modules/resource-management/infrastructure/resource-quotas-calendar/quotas-confirm-action'
import {
  FULL_DATE_FORMAT,
  FULL_DATE_TIME_FORMAT_WITH_T,
  getFormattedDate,
  getStartAndEndDayOfWeek,
  getRoundedTimeCompleted
} from '../../refactory/utils/dates'
import { TimeZoneOffsetTag } from '../../shared/time-zone-offset-tag'

const VIEW = 'view'
const EDIT = 'edit'

const CONFIRM_ACTION = {
  location: { top: 0, left: 0 },
  visible: false
}

const ArrowButton = styled(CustomButton)`
  button {
    padding: 0 0.5rem !important;
  }
`

const RightActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const AssignedQuotasTab = (props) => {
  const { resourceId, showCustomTooltip, bodyCustomTooltip, clientTimeZone, activeTab } = props
  const refSelectableGroup = useRef(null)
  const { t } = useTranslation()
  const store = useResourceProfileStore()
  const [showFleets, setShowFleets] = useState(false)
  const [selectedFleets, setSelectedFleets] = useState([])
  const [confirmAction, setConfirmAction] = useState(CONFIRM_ACTION)
  const [calendarQuotasMode, setCalendarQuotasMode] = useState(VIEW)
  const [selectedQuotas, setSelectedQuotas] = useState([])
  const [showDrawerCapacity, setShowDrawerCapacity] = useState(false)
  const [formAction, setFormAction] = useState('')

  const timeZone = useMemo(() => {
    if (store.resourceQuotas.fleets.length > 0) {
      return store.resourceQuotas.fleets[0].time_zone ?? clientTimeZone
    }
    return clientTimeZone
  }, [store.resourceQuotas.fleets, clientTimeZone])
  const [weekDay, setWeekDay] = useState(dayjs().tz(timeZone))

  const getTitle = () => {
    if (store.resourceQuotas && !store.resourceQuotas.loading) {
      const lastDateQuota = store.resourceQuotas.last_date_quota
      const month = dayjs(lastDateQuota).format('MMMM')
      const day = dayjs(lastDateQuota).format('D')
      return (
        <div className='assigned-quotas-tab-title'>
          {t('assignedQuotasUpTo', { date: `${t(month).toLowerCase()} ${t(day)}` })}
        </div>
      )
    }
  }

  const onChangeWeek = async (week) => {
    setWeekDay(week)
    const { fromInUTC: from, toInUTC: to } = getFromToInUTC(week.format(FULL_DATE_FORMAT))
    await store.loadQuotas(resourceId, from, to)
  }

  const clearItemsSelected = () => {
    const { current } = refSelectableGroup
    current && current.clearSelection()
  }

  const closeConfirmOption = () => {
    setConfirmAction({
      ...confirmAction,
      location: {
        top: 0,
        left: 0
      },
      visible: false
    })
  }

  const onFinish = (selectedItems) => {
    if (selectedItems && selectedItems.length > 0) {
      const lastItem = selectedItems[selectedItems.length - 1]
      const { bounds } = lastItem
      setConfirmAction({
        ...confirmAction,
        location: {
          top: bounds[0].top + 21,
          left: bounds[0].left + 35
        },
        visible: true
      })
    } else {
      closeConfirmOption()
    }
    setSelectedQuotas(selectedItems)
  }

  const onModeChange = () => {
    setCalendarQuotasMode(calendarQuotasMode === VIEW ? EDIT : VIEW)
    setFormAction('')
  }

  const onEditQuotas = async (fleetId = null) => {
    const { fromInUTC: from, toInUTC: to } = getFromToInUTC(weekDay.format(FULL_DATE_FORMAT))
    const quotas = mapQuotas(selectedQuotas, timeZone)
    const fleetsQuotas = getFleetsQuotas(selectedQuotas, timeZone)
    await store.deleteQuotasByResource(resourceId, fleetsQuotas, from, to)
    if (fleetId) {
      await store.createQuotasByResource(resourceId, fleetId, quotas, from, to)
    }
    closeConfirmOption()
    clearItemsSelected()
  }

  const getHeaderActions = () => {
    const isCurrentWeek = dayjs().isSame(weekDay, 'week')

    if (store.resourceFleets && store.resourceFleets.length > 0) {
      return (
        <div className='assigned-quotas-tab-header-actions'>
          <div className='left-actions'>
            {Can([SubModulePerms.RES_RESOURCE_QUOTA]) && (
              <CustomButton
                action={onModeChange}
                buttonText={calendarQuotasMode === VIEW ? t('editQuotas') : t('cancel')}
              />
            )}
            {calendarQuotasMode === VIEW ? (
              <DropdownMultiSelect
                items={store.resourceFleets}
                buttonText={`${t('fleets')} (${
                  store.resourceFleets.length === selectedFleets.length ? t('all') : selectedFleets.length
                })`}
                onChangeVisible={() => setShowFleets(!showFleets)}
                visible={showFleets}
                selectedItems={selectedFleets}
                onChangeSelectedItems={(selection) => {
                  setSelectedFleets(selection)
                }}
              />
            ) : (
              <DropdownSelect
                buttonText={t('editInBatch')}
                onChangeSelectedOption={(option) => {
                  setFormAction(option)
                  setShowDrawerCapacity(true)
                }}
                options={[
                  {
                    label: t('addQuotas'),
                    value: 'add'
                  },
                  {
                    label: t('removeQuotas'),
                    value: 'remove'
                  }
                ]}
                selectedOption={''}
              />
            )}
            <TimeZoneOffsetTag timeZone={timeZone} />
          </div>
          <RightActionsContainer className='right-actions'>
            {calendarQuotasMode === VIEW && (
              <>
                <ArrowButton
                  action={() => onChangeWeek(weekDay.subtract(7, 'days'))}
                  buttonText={<ArrowLeftOutlined />}
                />
                <CustomButton
                  action={() => onChangeWeek(dayjs())}
                  buttonText={t('currentWeek')}
                  disabled={isCurrentWeek}
                />
                <ArrowButton action={() => onChangeWeek(weekDay.add(7, 'days'))} buttonText={<ArrowRightOutlined />} />
              </>
            )}
          </RightActionsContainer>
        </div>
      )
    }
  }

  const getQuotasCalendar = () => {
    if (store.resourceQuotas && !store.resourceQuotas.loading) {
      return (
        <ResourceQuotasCalendar
          timezone={timeZone}
          mode={calendarQuotasMode}
          showCustomTooltip={showCustomTooltip}
          bodyCustomTooltip={bodyCustomTooltip}
          fleets={
            store.resourceQuotas.fleets.length > 0
              ? store.resourceQuotas.fleets
                  .map((fleet) => {
                    return {
                      ...fleet,
                      label: fleet.name,
                      value: fleet.id,
                      color: store?.resourceFleets?.find((rfleet) => rfleet.id === fleet.id)?.color ?? DEFAULT_COLORS[0]
                    }
                  })
                  .filter(({ value }) => selectedFleets.includes(value))
              : []
          }
          copy={t}
          weekday={weekDay}
          refSelectableGroup={refSelectableGroup}
          onFinishSelection={onFinish}
        >
          <QuotasConfirmAction
            {...confirmAction}
            fleets={store.resourceFleets && store.resourceFleets.length > 0 ? store.resourceFleets : []}
            onSelectedOption={onEditQuotas}
            onCleanOption={() => onEditQuotas()}
          />
        </ResourceQuotasCalendar>
      )
    }
  }

  const getFromToInUTC = (day) => {
    const { from, to } = getStartAndEndDayOfWeek(day)

    const fromInUTC = getFormattedDate({
      date: `${from} 00:00:00`,
      format: FULL_DATE_TIME_FORMAT_WITH_T,
      timezoneIn: timeZone,
      timezoneOut: 'Etc/UTC'
    })

    const toInUTC = getFormattedDate({
      date: getRoundedTimeCompleted(`${to} 23:59:59`),
      format: FULL_DATE_TIME_FORMAT_WITH_T,
      timezoneIn: timeZone,
      timezoneOut: 'Etc/UTC'
    })

    return { fromInUTC, toInUTC }
  }

  const CapacityParams = {
    initialValues: { id: resourceId },
    formAction,
    showDrawerCapacity,
    setShowDrawerCapacity: async (value) => {
      setShowDrawerCapacity(value)
      const { fromInUTC: from, toInUTC: to } = getFromToInUTC()
      await store.loadQuotas(resourceId, from, to)
    },
    timeZone,
    key: 'capacity-drawer'
  }

  useEffect(() => {
    const initialize = async () => {
      const { fromInUTC: from, toInUTC: to } = getFromToInUTC()

      await store.loadFleets(resourceId)
      setSelectedFleets(
        store.resourceFleets && store.resourceFleets.length > 0 ? store.resourceFleets.map(({ id }) => id) : []
      )
      store.loadQuotas(resourceId, from, to)
    }

    if (activeTab === '2') {
      initialize()
    }
    return () => {
      setCalendarQuotasMode(VIEW)
      setWeekDay(dayjs().tz(timeZone))
      setConfirmAction(CONFIRM_ACTION)
      setSelectedQuotas([])
      setShowDrawerCapacity(false)
      setFormAction('')
      setShowFleets(false)
    }
  }, [resourceId, activeTab, timeZone, store])

  return (
    <div className='assigned-quotas-tab'>
      {getTitle()}
      {getHeaderActions()}
      {getQuotasCalendar()}
      {CapacityParams.showDrawerCapacity && <DrawerFormCapacity {...CapacityParams} />}
    </div>
  )
}

export default observer(AssignedQuotasTab)
